import { ReceiptText, XCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

function formatEur(n) {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
  }).format(Number(n ?? 0));
}

/**
 * CreateExpensePreview — rich two-step approval card for CREATE_EXPENSE actions.
 *
 * Reemplaza el card genérico clave-valor para el registro de gastos: muestra
 * concepto + total, desglose base/IVA/IRPF, proveedor/categoría/fecha, y una
 * capa de avisos pre-confirmación (IVA no deducible, sin proveedor) — el dato
 * de mayor impacto fiscal antes de persistir.
 *
 * Lee claves crudas (las pasa PendingActionCard desde preview._raw).
 * Props: action { type, preview }, onApprove, onReject, status
 */
export default function CreateExpensePreview({ action, onApprove, onReject, status }) {
  const { preview } = action;

  if (status === 'approved') {
    return (
      <div className="flex items-center gap-2 p-2 bg-success/10 border border-success/30 rounded-lg text-xs text-success my-1">
        <ReceiptText className="w-3.5 h-3.5" />
        <span>Gasto aprobado — registrando…</span>
      </div>
    );
  }
  if (status === 'rejected') {
    return (
      <div className="flex items-center gap-2 p-2 bg-muted border border-border rounded-lg text-xs text-muted-foreground my-1">
        <XCircle className="w-3.5 h-3.5" />
        <span>Registro de gasto cancelado</span>
      </div>
    );
  }

  const irpf = preview?.irpf;
  const sinProveedor = typeof preview?.proveedor === 'string' && preview.proveedor.trim().startsWith('—');
  const noDeducible = preview?.deducible_iva === false;

  return (
    <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-950/20 p-4 space-y-3 my-2 max-w-sm">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Badge variant="outline" className="text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700 text-[10px] mb-1">
            Requiere aprobación
          </Badge>
          <p className="text-sm font-semibold text-foreground leading-tight truncate">
            {preview?.concepto ?? '—'}
          </p>
          {preview?.proveedor && (
            <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{preview.proveedor}</p>
          )}
        </div>
        <span className="text-lg font-bold text-amber-700 dark:text-amber-300 shrink-0 tabular-nums">
          {formatEur(preview?.importe)}
        </span>
      </div>

      {/* Breakdown */}
      <div className="flex flex-col gap-0.5 text-xs border-t border-amber-200 dark:border-amber-800 pt-2">
        <div className="flex justify-between text-muted-foreground">
          <span>Base imponible</span>
          <span className="tabular-nums">{formatEur(preview?.base_imponible)}</span>
        </div>
        <div className="flex justify-between text-muted-foreground">
          <span>IVA{preview?.tipo_iva != null ? ` (${preview.tipo_iva}%)` : ''}</span>
          <span className="tabular-nums">{formatEur(preview?.importe_iva)}</span>
        </div>
        {irpf && (
          <div className="flex justify-between text-muted-foreground">
            <span>IRPF{irpf.porcentaje != null ? ` (${irpf.porcentaje}%)` : ''}</span>
            <span className="tabular-nums">-{formatEur(irpf.importe)}</span>
          </div>
        )}
        <div className="flex justify-between font-semibold text-foreground">
          <span>Total</span>
          <span className="tabular-nums">{formatEur(preview?.importe)}</span>
        </div>
      </div>

      {/* Meta */}
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
        {preview?.categoria && <span>Categoría: {preview.categoria}</span>}
        {preview?.fecha && <span>Fecha: {preview.fecha}</span>}
        {preview?.numero_factura && <span>Nº: {preview.numero_factura}</span>}
      </div>

      {/* Avisos pre-confirmación */}
      {noDeducible && (
        <div className="flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-400 bg-amber-100/60 dark:bg-amber-900/30 rounded-md px-2 py-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
          <span>
            IVA <strong>no deducible</strong>
            {sinProveedor ? ' — sin proveedor/número de factura, Hacienda no admitirá la deducción en el modelo 303.' : '.'}
          </span>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onReject} className="flex-1 h-7 text-xs">
          <XCircle className="w-3 h-3 mr-1" />Cancelar
        </Button>
        <Button size="sm" onClick={onApprove} className="flex-1 h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white">
          <ReceiptText className="w-3 h-3 mr-1" />Registrar gasto
        </Button>
      </div>
    </div>
  );
}
